import type { PromptTemplateRow } from "../types/promptTemplate";

function str(v: unknown): string {
  if (v === null || v === undefined) return "";
  if (typeof v === "string") return v;
  return String(v);
}

function num(v: unknown, fallback: number): number {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function bool(v: unknown): boolean {
  if (v === true || v === 1) return true;
  if (v === false || v === 0) return false;
  if (typeof v === "string") {
    const s = v.trim().toLowerCase();
    return s === "true" || s === "t" || s === "1";
  }
  return false;
}

/**
 * Supabase/n8n podem enviar snake_case; alguns fluxos usam camelCase.
 * Garante um objeto alinhado ao que o painel espera.
 */
export function normalizePromptTemplateRow(raw: unknown): PromptTemplateRow | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;

  const id = str(o.id);
  if (!id) return null;

  const nicho =
    str(o.nicho).trim() ||
    str(o.Nicho).trim() ||
    str(o.niche).trim() ||
    str(o.Niche).trim();

  const instructions =
    str(o.instructions) || str(o.Instructions) || str(o.instruction) || "";

  const body_content =
    str(o.body_content) ||
    str(o.bodyContent) ||
    str(o.BodyContent) ||
    str(o.system_prompt) ||
    str(o.systemPrompt) ||
    "";

  const companyRaw = str(o.company_id || o.companyId).trim();

  let metadata: Record<string, unknown> | null = null;
  const m = o.metadata ?? o.Metadata;
  if (m !== null && m !== undefined) {
    if (typeof m === "object" && !Array.isArray(m)) {
      metadata = m as Record<string, unknown>;
    } else if (Array.isArray(m) && m.length === 0) {
      metadata = {};
    } else if (Array.isArray(m)) {
      metadata = { items: m };
    }
  }

  return {
    id,
    nicho,
    instructions,
    body_content,
    version: num(o.version ?? o.Version, 1),
    is_active: bool(o.is_active ?? o.isActive ?? o.active),
    metadata,
    company_id: companyRaw || null,
    created_at: str(o.created_at || o.createdAt) || undefined,
    updated_at: str(o.updated_at || o.updatedAt) || undefined,
  };
}

/** Ignora templates legados vinculados a uma loja — prompt é universal por nicho. */
export function isGlobalPromptTemplateRow(row: PromptTemplateRow): boolean {
  const cid = (row.company_id || "").trim();
  if (cid) return false;
  const meta = row.metadata;
  if (meta && typeof meta === "object") {
    const legacyCompany = str(meta.company_id || meta.companyId).trim();
    if (legacyCompany) return false;
  }
  return true;
}

export function normalizePromptTemplateList(raw: unknown): PromptTemplateRow[] {
  if (!Array.isArray(raw)) return [];
  const out: PromptTemplateRow[] = [];
  for (const item of raw) {
    const row = normalizePromptTemplateRow(item);
    if (row && isGlobalPromptTemplateRow(row)) out.push(row);
  }
  return out;
}
